import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle } from 'react-feather';
import { Badge } from 'reactstrap';
import { useChatbot } from './ChatbotContext';
import ChatbotHeader from './ChatbotHeader';
import ChatbotMessages from './ChatbotMessages';
import ChatbotInput from './ChatbotInput';
import ChatbotQuickActions from './ChatbotQuickActions';
import './chatbot.css';

const FAB_SIZE = 56;
const EDGE_MARGIN = 24;
const DRAG_THRESHOLD = 5; // px — abaixo disso é click, acima é drag
const STORAGE_KEY = 'chatbot_fab_position';

/**
 * Carrega posicao salva do localStorage ou retorna posicao padrao
 */
function loadPosition() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const pos = JSON.parse(saved);
      // Validar que a posicao esta dentro da tela
      if (typeof pos.bottom === 'number' && typeof pos.right === 'number') {
        return pos;
      }
    }
  } catch {
    // ignora
  }
  return { bottom: EDGE_MARGIN, right: EDGE_MARGIN };
}

/**
 * Widget principal do chatbot — FAB arrastavel + janela de chat
 * Renderiza em todas as paginas autenticadas
 */
const ChatbotWidget = () => {
  const { isOpen, isMinimized, messages, unreadCount, openChat, restoreChat } = useChatbot();

  // Posicao do FAB
  const [position, setPosition] = useState(loadPosition);
  const [isDragging, setIsDragging] = useState(false);

  // Refs para controle do drag
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startBottom: 0,
    startRight: 0,
    moved: false
  });
  const fabRef = useRef(null);

  // Verificar se usuario esta logado
  const isLogged = JSON.parse(localStorage.getItem('isLogged') || 'false');
  const chatbotEnabled = process.env.REACT_APP_CHATBOT_ENABLED !== 'false';

  /**
   * Clamp posicao para nao sair da tela — fica onde o usuario soltar
   */
  const clampPosition = useCallback((bottom, right) => {
    const maxBottom = window.innerHeight - FAB_SIZE - EDGE_MARGIN;
    const maxRight = window.innerWidth - FAB_SIZE - EDGE_MARGIN;

    return {
      bottom: Math.max(EDGE_MARGIN, Math.min(bottom, maxBottom)),
      right: Math.max(EDGE_MARGIN, Math.min(right, maxRight))
    };
  }, []);

  /**
   * Salva posicao no localStorage
   */
  const savePosition = useCallback((pos) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
      // ignora
    }
  }, []);

  /**
   * Handlers de mouse
   */
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startBottom: position.bottom,
      startRight: position.right,
      moved: false
    };
    setIsDragging(true);
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current.active) return;

    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;

    // Verificar se moveu o suficiente para ser drag
    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      dragRef.current.moved = true;
    }

    if (dragRef.current.moved) {
      const newRight = dragRef.current.startRight - deltaX;
      const newBottom = dragRef.current.startBottom - deltaY;

      // Clampar sem snap durante o drag (snap so ao soltar)
      const maxBottom = window.innerHeight - FAB_SIZE - EDGE_MARGIN;
      const maxRight = window.innerWidth - FAB_SIZE - EDGE_MARGIN;

      setPosition({
        bottom: Math.max(EDGE_MARGIN, Math.min(newBottom, maxBottom)),
        right: Math.max(EDGE_MARGIN, Math.min(newRight, maxRight))
      });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!dragRef.current.active) return;

    const wasDrag = dragRef.current.moved;
    dragRef.current.active = false;
    setIsDragging(false);

    if (wasDrag) {
      // Snap na borda mais proxima e salvar
      setPosition((prev) => {
        const snapped = clampPosition(prev.bottom, prev.right);
        savePosition(snapped);
        return snapped;
      });
    } else {
      // Foi um click — abrir o chat
      openChat();
    }
  }, [clampPosition, savePosition, openChat]);

  /**
   * Handlers de touch (mobile)
   */
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    dragRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      startBottom: position.bottom,
      startRight: position.right,
      moved: false
    };
    setIsDragging(true);
  }, [position]);

  const handleTouchMove = useCallback((e) => {
    if (!dragRef.current.active) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - dragRef.current.startX;
    const deltaY = touch.clientY - dragRef.current.startY;

    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      dragRef.current.moved = true;
    }

    if (dragRef.current.moved) {
      e.preventDefault(); // Evitar scroll durante drag
      const newRight = dragRef.current.startRight - deltaX;
      const newBottom = dragRef.current.startBottom - deltaY;

      const maxBottom = window.innerHeight - FAB_SIZE - EDGE_MARGIN;
      const maxRight = window.innerWidth - FAB_SIZE - EDGE_MARGIN;

      setPosition({
        bottom: Math.max(EDGE_MARGIN, Math.min(newBottom, maxBottom)),
        right: Math.max(EDGE_MARGIN, Math.min(newRight, maxRight))
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!dragRef.current.active) return;

    const wasDrag = dragRef.current.moved;
    dragRef.current.active = false;
    setIsDragging(false);

    if (wasDrag) {
      setPosition((prev) => {
        const snapped = clampPosition(prev.bottom, prev.right);
        savePosition(snapped);
        return snapped;
      });
    } else {
      openChat();
    }
  }, [clampPosition, savePosition, openChat]);

  /**
   * Registrar eventos globais de mouse/touch
   */
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  /**
   * Reajustar posicao ao redimensionar janela
   */
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        const maxBottom = window.innerHeight - FAB_SIZE - EDGE_MARGIN;
        const maxRight = window.innerWidth - FAB_SIZE - EDGE_MARGIN;
        return {
          bottom: Math.max(EDGE_MARGIN, Math.min(prev.bottom, maxBottom)),
          right: Math.max(EDGE_MARGIN, Math.min(prev.right, maxRight))
        };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isLogged || !chatbotEnabled) return null;

  // FAB (botao flutuante) — visivel quando o chat esta fechado
  if (!isOpen) {
    return (
      <button
        ref={fabRef}
        type="button"
        className={`chatbot-fab ${isDragging ? 'chatbot-fab-dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          bottom: `${position.bottom}px`,
          right: `${position.right}px`
        }}
        title="Copiloto Phada"
        aria-label="Abrir copiloto"
      >
        <MessageCircle size={24} />
        {unreadCount > 0 && (
          <Badge color="danger" pill className="chatbot-fab-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </button>
    );
  }

  // Chat minimizado — barra compacta
  if (isMinimized) {
    return (
      <div className="chatbot-minimized" onClick={restoreChat}>
        <MessageCircle size={16} />
        <span className="chatbot-minimized-title">Copiloto Phada</span>
        {unreadCount > 0 && (
          <Badge color="danger" pill className="chatbot-minimized-badge">
            {unreadCount}
          </Badge>
        )}
      </div>
    );
  }

  // Chat aberto — janela completa
  return (
    <div className="chatbot-window">
      <ChatbotHeader />
      <div className="chatbot-body">
        <ChatbotMessages />
        {messages.length === 0 && <ChatbotQuickActions />}
      </div>
      <ChatbotInput />
    </div>
  );
};

export default ChatbotWidget;
